import React, { useState, useEffect } from "react";
import { Spinner } from "reactstrap";
import isEmpty from "lodash/isEmpty";
import { useInput } from "@lib-shared/hooks";
import { appSettings } from "core/config";
import useFileManager from "core/hooks/use-file-manager";
import { convertToHtml } from "core/lib";
import { autoTextBoxHeightMax } from "core/lib/auto-textbox-height";
import Attachments from "./attachments";
import { ValidationMessage, PleaseWait } from "components/common-ui";
import SelectX from "components/ui/form/select";
import { Channels, TicketStatus, CaseCategoryOptions } from "../constants";

type NewQueryFormProps = {
  id: string,
  display: boolean,
  onSubmit: any,
  onClose: any,
  isLoading: boolean
}

const NewQueryForm = (props: NewQueryFormProps) => {
  const { id, display, onSubmit, onClose, isLoading } = props;
  const [isPosting, setIsPosting] = useState(false);
  const [isGlobalOversize, setIsGlobalOversize] = useState(false);
  const [isNotSupported, setIsNotSupported] = useState(() => false);

  //validations
  const inputValidation = (value: string) => !isEmpty(value);
  const subjectInput = useInput(inputValidation);
  const categoryInput = useInput(inputValidation);
  const descriptionInput = useInput(inputValidation);

  const inputs = [subjectInput, categoryInput, descriptionInput];

  const areInputsAllValid = () => inputs.find(x => !x.isValid) === undefined;

  const {
    handleAttachmentChange,
    uploadedFiles,
    reloadAttachment,
    removeAttachmentHandler,
    resetUploadedFiles,
  } = useFileManager([]);

  useEffect(() => {
    const isNotSupported = uploadedFiles.filter((file: any) => file.notSupported);
    const isOversize = uploadedFiles.filter((file: any) => file.overSize);

    setIsNotSupported(isNotSupported.length > 0);
    setIsGlobalOversize(isOversize.length > 0);
  }, [uploadedFiles]);

  let formIsValid = false;

  if (
    areInputsAllValid() &&
    !isGlobalOversize &&
    !isNotSupported
  ) {
    formIsValid = true;
  }

  const resetAll = () => {
    inputs.forEach(x => x.reset());

    resetUploadedFiles();

    (document.getElementById(id) as HTMLFormElement)?.reset();

    const description = document.getElementById("description") as HTMLTextAreaElement;

    if (description == null) {
      return;
    }

    description.rows = 1;
  }

  const closeForm = (e: any) => {
    resetAll();

    onClose();
  }

  const submitQuery = (e: any) => {
    e.preventDefault();

    if (!formIsValid) {
      return;
    }

    setIsPosting(true);

    const channelId = Channels.find((channel) => channel.name === "Online")?.id;

    const statusId = TicketStatus.find((status) => status.name === "Open")?.id;

    const attachments = uploadedFiles.filter((file: any) => file.file);

    const caseData = {
      subject: subjectInput.value,
      description: convertToHtml(descriptionInput.value),
      attachments: attachments,
      source: channelId,
      status: statusId,
      group_id: appSettings.freshDesk.groupId,
      priority: 1,
      custom_fields: { cf_d4b_query_type: categoryInput.value }
    };

    onSubmit(caseData).then((result: any) => {
      setIsPosting(false);

      if (result === true) {
        resetAll();
      }
    });
  }

  if (!display) {
    return null;
  }

  if (isPosting) {
    return (
      <PleaseWait message="Submitting your query" isInModal={true} />
    );
  }

  const submitCssClassName = "btn btn-black" + (formIsValid ? "" : " disabled");

  return (
    <form
      id={id}
      className="new-query floating query-form"
      onSubmit={submitQuery}>

      <div className="header-row">
        <h2 className="h5">
          Log a new query
        </h2>
      </div>

      <div className="row col">
        <input
          type="text"
          placeholder="Subject"
          id="subject"
          name="subject"
          value={subjectInput.value}
          onChange={subjectInput.valueChangeHandler}
          onBlur={subjectInput.inputBlurHandler}
        />

        <label htmlFor="subject">Subject</label>

        <ValidationMessage
          valid={!subjectInput.hasError}
          message="Please enter the subject"
        />
      </div>

      <div className="row col react-select">
        <SelectX options={CaseCategoryOptions}
          name="category"
          label="Category"
          useFloaty={true}
          isClearable={false}
          onHandleChange={(o) => { categoryInput.setValue(o?.value) }} />

        <ValidationMessage
          valid={!categoryInput.hasError}
          message="Please select the category"
        />
      </div>

      <div className="row col">
        <textarea
          placeholder="Description"
          id="description"
          name="description"
          value={descriptionInput.value}
          onInput={autoTextBoxHeightMax}
          rows={1}
          onChange={descriptionInput.valueChangeHandler}
          onBlur={descriptionInput.inputBlurHandler}
        />

        <label htmlFor="description">Description</label>

        <ValidationMessage
          valid={!descriptionInput.hasError}
          message="Please enter the description"
        />
      </div>

      <br />

      <Attachments
        uploadedFiles={uploadedFiles}
        removeAttachmentHandler={removeAttachmentHandler}
        reloadAttachment={reloadAttachment}
        handleAttachmentChange={handleAttachmentChange}
        showTitle={true} />

      <div className="row modal-buttons-row">
        <button type="button" className="btn btn-outline-black"
          onClick={closeForm}>Cancel</button>
        <button
          disabled={!formIsValid}
          type="button"
          onClick={submitQuery}
          className={submitCssClassName}>
          {isLoading && formIsValid ? (
            <Spinner
              color="dark"
              style={{ width: "1rem", height: "1rem" }}
            />
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </form>
  );
}

export default NewQueryForm;
